import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";

import PaymentStatusTemplate, {
  PaymentStatusType,
} from "../../../components/Payments/PaymentStatusTemplate/PaymentStatusTemplate";
import EventDispatcher from "../../../utils/event-dispatcher";
import {
  EventTypes,
  ROUTES,
  usePlayGameMetaDescription,
} from "../../../utils/constants";
import {
  extractOgTitleFromMainTitle,
  renderHeaders,
} from "../../../utils/renderHeaders";
import HourglassIcon from "../../../components/icons/HourglassIcon";
import CheckmarkCircleIconWithBorder from "../../../components/icons/CheckmarkCircleIconWithBorder";
import XCircleIcon from "../../../components/icons/XCircleIcon";
import useAnalytics from "../../../hooks/useAnalytics";
import {
  paymentStatusAtom,
  PaymentStatuses,
  topupInfoAtom,
} from "../../../jotai/atoms/topup";
import useEffectAfterMount from "../../../hooks/useEffectAfterMount";

const paymentStatusDetails = {
  [PaymentStatuses.PENDING]: {
    event: EventTypes.PAYMENTS.ERROR,
    title: "Payment pending",
    message:
      "Your payment is on the way. Your wallet will be credited if your payment is confirmed.",
    redirectUrl: ROUTES.WALLET.TOPUP_PENDING,
    icon: <HourglassIcon />,
  },
  [PaymentStatuses.SUCCESSFUL]: {
    event: EventTypes.PAYMENTS.SUCCESSFUL,
    title: "Payment confirmed",
    message: "Your payment has been confirmed",
    icon: <CheckmarkCircleIconWithBorder />,
  },
  [PaymentStatuses.FAILED]: {
    event: EventTypes.PAYMENTS.FAILED,
    title: "Payment unsuccessful",
    message:
      "Your payment was not successful. You will be redirected to retry top-up",
    redirectUrl: ROUTES.WALLET.TOPUP_FAILED,
    icon: <XCircleIcon />,
  },
};

export default function PaymentConfirmationStatus() {
  const navigate = useNavigate();
  const { sendCompleteTopupEvent, sendSuccessfulTopupEvent } = useAnalytics();
  const topupInfo = useAtomValue(topupInfoAtom);
  const paymentStatus = useAtomValue(paymentStatusAtom);
  const metaDescriptions = usePlayGameMetaDescription();
  const { t } = useTranslation();

  const paymentStatusInfo = useMemo(() => {
    if (!paymentStatus) return;

    let redirectUrl = "";
    switch (paymentStatus) {
      case PaymentStatuses.SUCCESSFUL:
        redirectUrl = ROUTES.WALLET.TOPUP_SUCCESSFUL_RETURN;
        break;
      case PaymentStatuses.PENDING:
      case PaymentStatuses.FAILED:
        redirectUrl = paymentStatusDetails[paymentStatus].redirectUrl;
    }

    return {
      event: paymentStatusDetails[paymentStatus].event,
      title: paymentStatusDetails[paymentStatus].title,
      message: paymentStatusDetails[paymentStatus].message,
      redirectUrl,
      icon: paymentStatusDetails[paymentStatus].icon,
    };
  }, [paymentStatus]);

  useEffect(() => {
    if (!paymentStatusInfo?.event) return;

    EventDispatcher.dispatch(paymentStatusInfo.event, {
      message: paymentStatusInfo.title,
    });
    const redirectTimeoutId = setTimeout(
      () => navigate(paymentStatusInfo.redirectUrl),
      3000,
    );
    return () => {
      clearTimeout(redirectTimeoutId);
    };
  }, [paymentStatusInfo, navigate]);

  useEffectAfterMount(() => {
    if (!topupInfo?.provider || !paymentStatus) return;
    const { provider, amount, price, isFirstTime, bonusHpr, gem } = topupInfo;

    sendCompleteTopupEvent({
      hpr_amount: amount,
      bonus_hpr_amount: bonusHpr ?? 0,
      gem_amount: gem ?? 0,
      fiat_amount: price ?? 0,
      first_time_top_up: isFirstTime,
      transaction_status: paymentStatus,
      payment_option: provider.toLowerCase(),
    });
    if (paymentStatus === PaymentStatuses.SUCCESSFUL) {
      sendSuccessfulTopupEvent({
        hpr_amount: amount,
        bonus_hpr_amount: bonusHpr ?? 0,
        gem_amount: gem ?? 0,
        fiat_amount: price ?? 0,
        first_time_top_up: isFirstTime,
        transaction_status: paymentStatus,
        payment_option: provider.toLowerCase(),
      });
    }
  }, [
    sendCompleteTopupEvent,
    sendSuccessfulTopupEvent,
    paymentStatus,
    topupInfo,
  ]);

  return (
    <>
      {renderHeaders({
        title: t("metadata:top_up_status_hyper_gaming_app"),
        description: metaDescriptions.generic,
        ogTitle: extractOgTitleFromMainTitle(
          t("metadata:top_up_status_hyper_gaming_app"),
        ),
      })}

      <PaymentStatusTemplate
        type={PaymentStatusType.DONE}
        title={paymentStatusInfo?.title ?? ""}
        message={paymentStatusInfo?.message}
        eventType={paymentStatusInfo?.event}
        navigateUrl={paymentStatusInfo?.redirectUrl}
        icon={paymentStatusInfo?.icon}
      />
    </>
  );
}
